import { useState } from 'react';
import { motion } from 'framer-motion';
import { ModalFrame } from '../ui/modal';
import { IconClose, IconReport } from '../ui/icons';
import { CATEGORIES, CATEGORY_EXAMPLES, colorFor } from './changeTypes';
import { sydneyToInstant, instantToSydneyParts, nowSydneyParts, formatWhen } from './time';
import '../po/drawer.css';

// Add / edit a change entry. Any staff with the page may write (per the
// decision) — no admin gate here. Save hands a plain row up to the parent,
// which mutates local state and logs activity (frontend-first phase).
export default function WebChangeModal({ session, change, projects, defaultProjectId, onClose, onSave }) {
  const isEdit = !!change?.id;
  const seedParts = isEdit ? instantToSydneyParts(change.changed_at) : nowSydneyParts();
  const [form, setForm] = useState(() => ({
    project_id: change?.project_id || defaultProjectId || projects[0]?.id || '',
    date: seedParts.date,
    time: seedParts.time,
    change_type: change?.change_type || 'website',
    action: change?.action || '',
    requested_by: change?.requested_by || '',
    action_by: change?.action_by || session?.name || '',
    notes: change?.notes || '',
  }));
  const [saving, setSaving] = useState(false);
  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function save() {
    if (!form.action.trim()) { alert('Describe the change (Action).'); return; }
    if (!form.project_id) { alert('Pick a project.'); return; }
    setSaving(true);
    const row = {
      ...(isEdit ? { id: change.id } : {}),
      project_id: form.project_id,
      changed_at: sydneyToInstant(form.date, form.time),
      change_type: form.change_type,
      action: form.action.trim(),
      requested_by: form.requested_by.trim() || null,
      action_by: form.action_by.trim() || null,
      notes: form.notes.trim() || null,
    };
    onSave(row, isEdit);
    onClose();
  }

  return (
    <ModalFrame onClose={onClose}>
      <motion.div
        role="dialog"
        initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }} transition={{ duration: 0.18 }}
        style={{ width: 'min(680px, calc(100vw - 48px))', maxHeight: 'calc(100vh - 96px)', background: '#fff', borderRadius: 16, boxShadow: '0 24px 60px rgba(15,20,25,0.22), 0 2px 6px rgba(15,20,25,0.08)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subdued)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--surface-inverted)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <IconReport />
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-emphasized)' }}>{isEdit ? 'Edit Change' : 'Log a Change'}</div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subdued)', padding: 6, display: 'flex' }}>
            <IconClose size={16} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div>
              <div className="form-group"><label className="form-label">Project</label>
                <select className="form-select" value={form.project_id} onChange={e => setField('project_id', e.target.value)}>
                  {projects.filter(p => p.active).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Type</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: colorFor(form.change_type), flexShrink: 0 }} />
                  <select className="form-select" style={{ flex: 1 }} value={form.change_type} onChange={e => setField('change_type', e.target.value)}>
                    {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                {CATEGORY_EXAMPLES[form.change_type] && (
                  <div style={{ fontSize: 11, color: 'var(--text-subdued)', marginTop: 6 }}>e.g. {CATEGORY_EXAMPLES[form.change_type]}</div>
                )}
              </div>
              <div className="form-group"><label className="form-label">Date <span className="field-hint">Sydney</span></label>
                <input type="date" className="form-input" value={form.date} onChange={e => setField('date', e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Time <span className="field-hint">Sydney</span></label>
                <input type="time" className="form-input" value={form.time} onChange={e => setField('time', e.target.value)} />
              </div>
            </div>

            <div>
              <div className="form-group"><label className="form-label">Action</label>
                <textarea className="form-textarea" style={{ minHeight: 96 }} value={form.action} onChange={e => setField('action', e.target.value)} placeholder="What changed? e.g. Science page is now live" />
              </div>
              <div className="form-group"><label className="form-label">Requested by <span className="field-hint">optional</span></label>
                <input className="form-input" value={form.requested_by} onChange={e => setField('requested_by', e.target.value)} placeholder="e.g. Mo, Emily, Nik" />
              </div>
              <div className="form-group"><label className="form-label">Action by <span className="field-hint">optional</span></label>
                <input className="form-input" value={form.action_by} onChange={e => setField('action_by', e.target.value)} placeholder="Who made the change" />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Notes <span className="field-hint">optional</span></label>
                <textarea className="form-textarea" value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="Extra context" />
              </div>
            </div>
          </div>

          {isEdit && change.created_at && (
            <div className="muted-small" style={{ marginTop: 12 }}>Created {formatWhen(change.created_at)}{change.created_by ? ` by ${change.created_by}` : ''}</div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, padding: '14px 20px', borderTop: '1px solid var(--border-subdued)', flexShrink: 0 }}>
          <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={saving} onClick={save}>{saving ? 'Saving...' : isEdit ? 'Save' : 'Log Change'}</button>
        </div>
      </motion.div>
    </ModalFrame>
  );
}
